import React, { useState, useEffect,useMemo } from 'react';
import { TextField, Paper, Typography, Box } from '@mui/material';
import { Tiers, FamilleData } from '../../models/models';
import { ButtonType, TypeTiers, GeneriqueDataType } from '../../constants/enums';
import { createTiers, deleteTiers, setSelectedTiers } from '../../reduxStore/TiersSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../reduxStore/Store';
import { useSnackbar } from '../../components/SnackbarProvider';
import MyButton from '../MyButton';
import { labels } from '../../constants/labels';
import TiersPlafondSection from './TiersPlafondSection';
import FamilleAutoC from '../AutoComplete/FamilleAutoC';
import SelectDepartementsPartage, { parsePartageIds } from '../Selects/SelectDepartementsPartage';

/** Type de catégorie generique_data associé à une nature de tiers (null si non catégorisable). */
const categorieTypeForTiers = (t: TypeTiers): GeneriqueDataType | null =>
  t === TypeTiers.CLIENT      ? GeneriqueDataType.CATEGORIE_CLIENT
  : t === TypeTiers.FOURNISSEUR ? GeneriqueDataType.CATEGORIE_FOURNISSEUR
  : null;



interface TiersFormProps {
  tiersType: TypeTiers;
  onCancel: () => void;
}


const TiersForm: React.FC<TiersFormProps> = ({ tiersType, onCancel }) => {

  const { selectedTiers } = useSelector((state: any) => state.TiersSlice);
  const initialiseFormData = useMemo(() => ({
    id: -1,
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    adresse: '',
    type: tiersType,
    categorie_id: null,
    departements_partage: [] as number[],
  }), [tiersType]);

  const [formData, setFormData] = useState<Tiers>(initialiseFormData);
  // Catégorie sélectionnée (objet pour l'autocomplete) — type piloté par tiersType.
  const [selectedCategorie, setSelectedCategorie] = useState<FamilleData | null>(null);
  const categorieType = categorieTypeForTiers(tiersType);

  useEffect(() => {
    if (selectedTiers) {
      setFormData(
        {
          id: selectedTiers.id,
          nom: selectedTiers.nom,
          prenom: selectedTiers.prenom,
          email: selectedTiers.email,
          telephone: selectedTiers.telephone,
          adresse: selectedTiers.adresse,
          type: tiersType, // Default value for type
          categorie_id: selectedTiers.categorie_id ?? null,
          departements_partage: parsePartageIds(selectedTiers.departements_partage_ids),
        }
      );
      setSelectedCategorie(
        selectedTiers.categorie_id
          ? { id: selectedTiers.categorie_id, famille_row_id: 0, nom: selectedTiers.categorie_nom ?? '', visible: true } as FamilleData
          : null
      );
    } else {
      setFormData(initialiseFormData);
      setSelectedCategorie(null);
    }

  }, [selectedTiers, tiersType,initialiseFormData]); // Dependencies: initialData and selecselectedTiers


  const dispatch = useDispatch<AppDispatch>(); // Typing dispatch
  const { showSnackbar } = useSnackbar();

  // Libellé selon le type de tiers (client / fournisseur / employé)
  const typeLabel =
    tiersType === TypeTiers.FOURNISSEUR ? labels.fournisseur
    : tiersType === TypeTiers.EMPLOYE ? labels.employe
    : labels.client;
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    
    try {
      await dispatch(createTiers(formData)).unwrap(); // Wait for the action to finish
      showSnackbar("operassion reussite!", "success");
      dispatch(setSelectedTiers(null))
      setFormData(initialiseFormData);
      setSelectedCategorie(null);
    } catch (error) {
      showSnackbar(error as string || "Failed to create tiers", "error");
    }
  };

  const handleDelete = async (tiers: Tiers) => {
    if (selectedTiers) {
      if (window.confirm("Êtes-vous sûr de vouloir supprimer ce tiers ?")) {
        try {
          await dispatch(deleteTiers(tiers.id ?? 0)).unwrap();
          showSnackbar("Tiers supprimé!", "success");
          dispatch(setSelectedTiers(null));
          setFormData(initialiseFormData);
          setSelectedCategorie(null);
        } catch (error) {
          showSnackbar(error as string || "Échec de la suppression", "error");
        }
      }
    }

  }

  return (
    <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
      <Typography variant="h6" gutterBottom>
        {selectedTiers ? `Editer un ${typeLabel}` : `Ajouter un ${typeLabel}`}
      </Typography>
      <form onSubmit={handleSubmit}>
        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
          <TextField name="nom" label="Nom" value={formData.nom} onChange={handleChange} fullWidth size="small" />
          <TextField name="prenom" label="Prénom" value={formData.prenom} onChange={handleChange} fullWidth size="small" />
          <TextField name="email" label="Email" value={formData.email} onChange={handleChange} fullWidth size="small" />
          <TextField name="telephone" label="Téléphone" value={formData.telephone} onChange={handleChange} fullWidth size="small" />
          <TextField name="adresse" label="Adresse" value={formData.adresse || ''} onChange={handleChange} fullWidth size="small" />
          {/* Catégorie — type piloté par la nature du tiers (client / fournisseur) */}
          {categorieType && (
            <FamilleAutoC
              slot="main"
              type={categorieType}
              label="Catégorie"
              selectedFamille={selectedCategorie}
              onFamilleChange={(c) => {
                setSelectedCategorie(c);
                setFormData((prev) => ({ ...prev, categorie_id: c ? c.id : null }));
              }}
            />
          )}
          {/* Partage par département — vide = tous */}
          <SelectDepartementsPartage
            value={formData.departements_partage ?? []}
            onChange={(ids) => setFormData((prev) => ({ ...prev, departements_partage: ids }))}
          />
        </Box>
        {/* Hidden fields for owner_id and user_id */}
        <input type="hidden" name="type" value={formData.type} />
        <input type="hidden" name="owner_id" value={formData.owner_id} />
        <input type="hidden" name="user_id" value={formData.user_id} />
        <Box mt={2} display="flex" justifyContent="space-between">
          <div>
            <MyButton actionType={ButtonType.INSERT} variant="contained" onClick={handleSubmit}>
              {selectedTiers ? 'Modifier' : 'Ajouter'}
            </MyButton>
            <MyButton actionType={ButtonType.DELETE} variant="outlined" onClick={() => handleDelete(selectedTiers)}>
              Delete
            </MyButton>
            <MyButton actionType={ButtonType.CANCEL} variant="outlined" onClick={onCancel}>
              Cancel
            </MyButton>
          </div>
        </Box>
      </form>

      {/* Plafonds — uniquement visible sur un tier déjà persisté (besoin d'un id). */}
      {selectedTiers?.id && selectedTiers.id > 0 && (
        <TiersPlafondSection tiersId={selectedTiers.id} />
      )}
    </Paper>
  );
};

export default TiersForm;
